import { useState, useRef, useCallback } from 'react';
import { useCameraStream } from '../shared';

interface PhotoState {
  photos: string[];          // data-URL strings
  photoBlobs: Blob[];
  zoomLevel: number;
  maxZoom: number;
  exposure: number;
  exposureRange: { min: number; max: number } | null;
}

export const usePhotoCapture = () => {
  const camera = useCameraStream({
    audio: false,
    width: 1920,
    height: 1080,
  });

  const [state, setState] = useState<PhotoState>({
    photos: [],
    photoBlobs: [],
    zoomLevel: 1,
    maxZoom: 1,
    exposure: 0,
    exposureRange: null,
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const maxZoomRef = useRef(1);
  const exposureRangeRef = useRef<{ min: number; max: number } | null>(null);

  /** Read zoom / exposure capabilities from the active video track */
  const readCapabilities = useCallback((stream: MediaStream) => {
    const track = stream.getVideoTracks()[0];
    trackRef.current = track;

    let newMaxZoom = 1;
    let newExposureRange: { min: number; max: number } | null = null;
    try {
      const caps = track.getCapabilities() as any;
      if (caps.zoom) newMaxZoom = caps.zoom.max;
      if (caps.exposureCompensation) {
        newExposureRange = { min: caps.exposureCompensation.min, max: caps.exposureCompensation.max };
      }
    } catch { /* capabilities unsupported */ }

    maxZoomRef.current = newMaxZoom;
    exposureRangeRef.current = newExposureRange;

    setState(prev => ({
      ...prev,
      zoomLevel: 1,
      maxZoom: newMaxZoom,
      exposure: 0,
      exposureRange: newExposureRange,
    }));
  }, []);

  /** Start the camera stream and read device capabilities */
  const startCamera = useCallback(async (facing?: 'user' | 'environment') => {
    const stream = await camera.startStream(facing);
    if (stream) readCapabilities(stream);
  }, [camera.startStream, readCapabilities]);

  /** Flip between front / rear camera */
  const flipCamera = useCallback(async () => {
    const stream = await camera.toggleCamera();
    if (stream) readCapabilities(stream);
  }, [camera.toggleCamera, readCapabilities]);

  /** Apply zoom level to the active track */
  const applyZoom = useCallback((newZoom: number) => {
    const clamped = Math.min(Math.max(newZoom, 1), maxZoomRef.current);
    setState(prev => ({ ...prev, zoomLevel: clamped }));
    if (!trackRef.current) return;
    try {
      trackRef.current.applyConstraints({ advanced: [{ zoom: clamped } as any] });
    } catch { /* zoom unsupported */ }
  }, []);

  /** Apply exposure compensation (-2 … +2 mapped to device range) */
  const applyExposure = useCallback((value: number) => {
    setState(prev => ({ ...prev, exposure: value }));
    if (!trackRef.current || !exposureRangeRef.current) return;
    const { min, max } = exposureRangeRef.current;
    const mapped = min + ((value + 2) / 4) * (max - min);
    try {
      trackRef.current.applyConstraints({ advanced: [{ exposureCompensation: mapped } as any] });
    } catch { /* exposure unsupported */ }
  }, []);

  /** Capture a frame from the live video into a data-URL + Blob */
  const takePhoto = useCallback(() => {
    const video = camera.videoRef.current;
    if (!video) return;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    canvas.toBlob(
      blob => {
        if (blob) {
          setState(prev => ({
            ...prev,
            photos: [...prev.photos, dataUrl],
            photoBlobs: [...prev.photoBlobs, blob],
          }));
        }
      },
      'image/jpeg',
      0.92,
    );
  }, [camera.videoRef]);

  /** Add photos from file input (gallery upload), re-encoded to tame size.
      Camera captures already go through canvas JPEG; gallery files arrived
      RAW — 5-15 MB per modern phone photo. Cap the long edge at 2048px,
      JPEG 0.9 (visually lossless for research review, typically ~0.5 MB).
      Falls back to the original when the browser can't decode the format
      (e.g. HEIC on Chrome) or when re-encoding doesn't actually shrink it. */
  const addPhotosFromFiles = useCallback((files: FileList) => {
    const MAX_DIM = 2048;
    const recode = async (file: File): Promise<Blob> => {
      try {
        const bmp = await createImageBitmap(file, {
          imageOrientation: 'from-image',
        } as ImageBitmapOptions);
        const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bmp.width * scale);
        canvas.height = Math.round(bmp.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
        bmp.close();
        const blob = await new Promise<Blob | null>(res =>
          canvas.toBlob(res, 'image/jpeg', 0.9),
        );
        return blob && blob.size < file.size ? blob : file;
      } catch {
        return file;
      }
    };

    Array.from(files).forEach(async file => {
      const blob = await recode(file);
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setState(prev => ({
          ...prev,
          photos: [...prev.photos, dataUrl],
          photoBlobs: [...prev.photoBlobs, blob],
        }));
      };
      reader.readAsDataURL(blob);
    });
  }, []);

  /** Inject pre-existing photos (e.g. from a restored draft) */
  const injectPhotos = useCallback((blobs: Blob[]) => {
    blobs.forEach(blob => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setState(prev => ({
          ...prev,
          photos: [...prev.photos, dataUrl],
          photoBlobs: [...prev.photoBlobs, blob],
        }));
      };
      reader.readAsDataURL(blob);
    });
  }, []);

  /** Remove a photo by index */
  const deletePhoto = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
      photoBlobs: prev.photoBlobs.filter((_, i) => i !== index),
    }));
  }, []);

  /** Stop the camera stream */
  const stopCamera = useCallback(() => {
    camera.stopStream();
  }, [camera.stopStream]);

  /** Reset all state */
  const reset = useCallback(() => {
    camera.stopStream();
    setState({
      photos: [],
      photoBlobs: [],
      zoomLevel: 1,
      maxZoom: 1,
      exposure: 0,
      exposureRange: null,
    });
  }, [camera.stopStream]);

  return {
    ...state,
    isReady: camera.isReady,
    facingMode: camera.facingMode,
    error: camera.error,
    videoRef: camera.videoRef,
    trackRef,
    startCamera,
    flipCamera,
    takePhoto,
    addPhotosFromFiles,
    injectPhotos,
    deletePhoto,
    stopCamera,
    reset,
    applyZoom,
    applyExposure,
  };
};
